import { NavLink, Outlet, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { LayoutDashboard, Users2, Kanban, UserCog, Settings, LogOut, Bell, Menu, X, ChevronRight, Mail } from "lucide-react";
import { useAuth } from "./AuthContext";
import { adminHttp } from "../lib/adminApi";

const NAV_ITEMS = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/admin/leads", label: "Leads", icon: Users2 },
  { to: "/admin/pipeline", label: "Pipeline", icon: Kanban },
  { to: "/admin/emails", label: "Emails", icon: Mail },
  { to: "/admin/users", label: "Usuarios", icon: UserCog, adminOnly: true },
  { to: "/admin/settings", label: "Ajustes", icon: Settings },
];

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const [newCount, setNewCount] = useState(0);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    let mounted = true;
    adminHttp.get("/admin/leads?status=new&limit=1")
      .then(({ data }) => { if (mounted) setNewCount(data.total || 0); })
      .catch(() => {});
    return () => { mounted = false; };
  }, [location.pathname]);

  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  const breadcrumb = buildCrumb(location.pathname);

  return (
    <div className="min-h-screen flex bg-eclipse text-[#1F2937] font-sans" data-testid="admin-layout">
      {/* Sidebar */}
      <aside
        className={`fixed lg:static inset-y-0 left-0 z-40 w-64 bg-white border-r border-[#DDE2D5] flex flex-col transform transition-transform ${
          mobileOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
        }`}
        data-testid="admin-sidebar"
      >
        <div className="px-6 py-6 border-b border-[#DDE2D5] flex items-center gap-3">
          <img src="/images/brand/vt_logo_lightbg.png" alt="Velarias Tens" className="h-10 w-auto" />
          <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#3D5311] font-semibold">Admin</span>
        </div>

        <nav className="flex-1 p-4 space-y-1">
          {NAV_ITEMS.filter((n) => !n.adminOnly || user?.role === "admin").map((n) => {
            const Icon = n.icon;
            const isEmailSection = n.to === "/admin/emails";
            return (
              <div key={n.to}>
                <NavLink
                  to={n.to}
                  end={n.end}
                  data-testid={`admin-nav-${n.label.toLowerCase()}`}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-4 py-3 font-mono uppercase text-xs tracking-wider transition-colors border-l-2 ${
                      isActive
                        ? "bg-[#F2F4ED] text-accent border-accent"
                        : "text-[#4B5563] hover:text-[#1F2937] hover:bg-[#F2F4ED] border-transparent"
                    }`
                  }
                >
                  <Icon className="w-4 h-4" />
                  <span>{n.label}</span>
                </NavLink>
                {isEmailSection && location.pathname.startsWith("/admin/emails") && (
                  <div className="ml-9 mt-1 space-y-0.5">
                    {[
                      { to: "/admin/emails/logs", label: "Logs" },
                      { to: "/admin/emails/scheduled", label: "Programados" },
                      { to: "/admin/settings/emails", label: "Configuración" },
                    ].map((s) => (
                      <NavLink key={s.to} to={s.to}
                        className={({ isActive }) =>
                          `block py-1.5 pl-3 font-mono uppercase text-[10px] tracking-wider border-l border-[#DDE2D5] transition ${isActive ? "text-accent border-accent" : "text-[#6B7280] hover:text-[#1F2937]"}`
                        }
                        data-testid={`admin-subnav-${s.label.toLowerCase()}`}
                      >
                        {s.label}
                      </NavLink>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </nav>

        <div className="p-4 border-t border-[#DDE2D5]">
          <div className="px-3 py-3 bg-[#F2F4ED] border border-[#EAECEA]">
            <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#6B7280]">Sesión</p>
            <p className="mt-1 text-sm text-[#1F2937] truncate" data-testid="admin-user-email">{user?.email}</p>
            <p className="text-[11px] font-mono text-[#3D5311] uppercase tracking-wider">{user?.role}</p>
          </div>
          <button
            onClick={logout}
            className="mt-3 w-full flex items-center gap-2 px-3 py-2 text-[#374151] hover:text-accent font-mono uppercase text-xs tracking-wider"
            data-testid="admin-logout"
          >
            <LogOut className="w-4 h-4" /> Salir
          </button>
        </div>
      </aside>

      {/* Main content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Top bar */}
        <header className="bg-white/95 backdrop-blur border-b border-[#DDE2D5] sticky top-0 z-30">
          <div className="flex items-center justify-between px-4 lg:px-8 h-16">
            <div className="flex items-center gap-3">
              <button
                className="lg:hidden text-[#374151] hover:text-accent"
                onClick={() => setMobileOpen((v) => !v)}
                aria-label="Menú"
                data-testid="admin-mobile-toggle"
              >
                {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              </button>
              <nav className="flex items-center gap-1 text-xs font-mono uppercase tracking-widest text-[#6B7280]" data-testid="admin-breadcrumb">
                {breadcrumb.map((c, i) => (
                  <span key={c} className="flex items-center gap-1">
                    {i > 0 && <ChevronRight className="w-3 h-3" />}
                    <span className={i === breadcrumb.length - 1 ? "text-accent" : ""}>{c}</span>
                  </span>
                ))}
              </nav>
            </div>

            <div className="flex items-center gap-4">
              <NavLink
                to="/admin/leads?status=new"
                className="relative hover:text-accent transition-colors"
                data-testid="admin-new-leads-indicator"
              >
                <Bell className="w-5 h-5 text-[#374151]" />
                {newCount > 0 && (
                  <span className="absolute -top-1 -right-1 bg-accent text-white text-[10px] font-black w-4 h-4 grid place-items-center rounded-full">
                    {newCount > 9 ? "9+" : newCount}
                  </span>
                )}
              </NavLink>
              <div className="w-9 h-9 bg-accent/20 border border-accent/40 grid place-items-center text-accent font-display font-bold">
                {user?.full_name?.slice(0, 1) || "?"}
              </div>
            </div>
          </div>
        </header>

        <main className="flex-1 overflow-auto" data-testid="admin-main">
          <Outlet />
        </main>
      </div>

      {/* Mobile backdrop */}
      {mobileOpen && <div className="lg:hidden fixed inset-0 z-30 bg-black/40" onClick={() => setMobileOpen(false)} />}
    </div>
  );
}

function buildCrumb(pathname) {
  const map = {
    "/admin": ["Admin", "Dashboard"],
    "/admin/leads": ["Admin", "Leads"],
    "/admin/pipeline": ["Admin", "Pipeline"],
    "/admin/emails": ["Admin", "Emails"],
    "/admin/emails/logs": ["Admin", "Emails", "Logs"],
    "/admin/emails/scheduled": ["Admin", "Emails", "Programados"],
    "/admin/settings/emails": ["Admin", "Ajustes", "Emails"],
    "/admin/users": ["Admin", "Usuarios"],
    "/admin/settings": ["Admin", "Ajustes"],
  };
  if (pathname.startsWith("/admin/leads/")) return ["Admin", "Leads", "Detalle"];
  return map[pathname] || ["Admin"];
}
